/* eslint-disable jsx-a11y/alt-text */
import { useCallback, useEffect, useMemo } from "react"

import * as turf from "@turf/turf"
import { useMatch, useParams } from "react-router-dom"

import { createMapIcon, Source, useMapDispatchContext } from "@l2r-front/l2r-map"
import { useNetworkReferential, useNetworksStateContext } from "@l2r-front/l2r-networks"
import { useTheme } from "@l2r-front/l2r-ui"
import { useUrlParams } from "@l2r-front/l2r-utils"

import { OBJECT_TYPE_FILTER } from "../../constants/filters"
import { API_MODULE_DOCUMENT, APP_DOCUMENT, MODULE_DOCUMENTS, ModulesInfo, NETWORK_ATTACHMENT, ROAD_ATTACHMENT, SECTION_ATTACHMENT } from "../../constants/modules"
import { DOCUMENTS_COUNT_PER_PAGE } from "../../constants/pages"
import { NETWORK_DOCUMENTS_PAGE, ROAD_DOCUMENTS_PAGE } from "../../constants/urlParams"
import { useDocumentStateContext } from "../../contexts/DocumentContext"
import { useDocuments } from "../../hooks/queries/useDocument"

declare global {
    interface Window {
        generatedIcons?: Record<string, string>
    }
}

type DocumentsSourceProps = {
    iconOffset?: number
}

export function DocumentsSource(props: DocumentsSourceProps) {

    const {
        iconOffset = 28,
        ...otherProps
    } = props

    const { attachmentId, road } = useParams()
    const matchNetwork = useMatch(`/:networkSlug/${APP_DOCUMENT}/${MODULE_DOCUMENTS}/attached/${NETWORK_ATTACHMENT}`)
    const matchDetail = useMatch(`/:networkSlug/${APP_DOCUMENT}/${MODULE_DOCUMENTS}/:road/attached/*`)
    const { getParam } = useUrlParams()
    const pageUrlParam = (matchDetail || matchNetwork) ? null : road ? ROAD_DOCUMENTS_PAGE : NETWORK_DOCUMENTS_PAGE
    const { getMapRef } = useMapDispatchContext()
    const theme = useTheme()
    const { apiFilters } = useDocumentStateContext()
    const matchSection = useMatch(`/:networkSlug/${APP_DOCUMENT}/${MODULE_DOCUMENTS}/:road/attached/${SECTION_ATTACHMENT}`)
    const match = useMatch(`/:networkSlug/${APP_DOCUMENT}/${MODULE_DOCUMENTS}/:road/attached/:attachmentType/*`)
    const attachmentType = matchNetwork ? NETWORK_ATTACHMENT : matchSection ? SECTION_ATTACHMENT : match ? match.params.attachmentType : null
    const { selectedNetwork, selectedSubNetworkSlugs } = useNetworksStateContext()

    const selectedNetworkBoundingBox = useMemo(() => {

        if (!selectedNetwork) {
            return null
        }

        if (!selectedSubNetworkSlugs?.length) {
            return turf.bbox(selectedNetwork?.["boundingBox"])
        } else {
            const bboxes = selectedSubNetworkSlugs.map(subNetwork => {
                return selectedNetwork?.["children"].find(child => child.slug === subNetwork)
            })
                ?.filter(child => child.modules?.includes(API_MODULE_DOCUMENT))
                .map(sn => turf.bbox(sn.boundingBox))

            return bboxes.reduce((acc, bbox) => {
                return [
                    Math.min(acc[0], bbox[0]),
                    Math.min(acc[1], bbox[1]),
                    Math.max(acc[2], bbox[2]),
                    Math.max(acc[3], bbox[3]),
                ]
            }, [Infinity, Infinity, -Infinity, -Infinity])
        }
    }, [selectedNetwork, selectedSubNetworkSlugs])

    const page = useMemo(() => {
        return parseInt(getParam(pageUrlParam) || "1") || 1
    }, [getParam, pageUrlParam])

    const { data } = useDocuments(
        {
            as_geojson: true,
            ...(road && { linear_location_road: road }),
            ...(!attachmentId && { offset: (page - 1) * DOCUMENTS_COUNT_PER_PAGE }),
            ...(!attachmentId && { limit: DOCUMENTS_COUNT_PER_PAGE }),
            ...(!!attachmentId && { attached_object_uuid: attachmentId }),
            ...(!!matchNetwork && { [OBJECT_TYPE_FILTER]: NETWORK_ATTACHMENT }),
            ...(apiFilters && { ...apiFilters }),
            ...((attachmentType === SECTION_ATTACHMENT || attachmentType === ROAD_ATTACHMENT) && { [OBJECT_TYPE_FILTER]: attachmentType.toUpperCase() }),
        })

    const { data: referential } = useNetworkReferential()

    const createIcon = useCallback(async (type, map, theme) => {
        const color = theme.palette[ModulesInfo?.[type as keyof typeof ModulesInfo]?.color]?.main
        const iconId = type
        const module = ModulesInfo[type]
        createMapIcon(type, map, theme, iconOffset, color, module, iconId)
    }, [iconOffset])

    useEffect(() => {
        const map = getMapRef()?.getMap()

        if (!map || !theme) {
            return undefined
        }

        if (!window.generatedIcons) {
            window.generatedIcons = {}
        }
        Object.keys(ModulesInfo).forEach(documentType => {
            if (!window.generatedIcons || !window.generatedIcons[documentType]) {
                createIcon(documentType, map, theme)
            }
        })

        interface StyleImageMissingEvent {
            id: string
        }

        const loadIcon = (e: StyleImageMissingEvent) => {
            const type: string = e.id

            if (!type) {
                return
            }

            if (!window.generatedIcons) {
                window.generatedIcons = {}
            }

            if (window.generatedIcons[type]) {
                (map).loadImage(
                    window.generatedIcons[type],
                    (error: Error | null, icon) => {
                        if (error) {
                            return console.error(error)
                        }

                        if (!(map).hasImage(type)) {
                            (map).addImage(type, icon)
                        }
                    },
                )
            } else {
                createIcon(type, map, theme)
            }
        }

        map?.on("styleimagemissing", loadIcon)

        return () => map?.off("styleimagemissing", loadIcon)

    }, [createIcon, getMapRef, theme])

    const sanitizedData = useMemo(() => {
        const features = data?.features || data?.results
        if (!features || !referential) {
            return null
        }
        const seenUuids = new Set()
        const sanitizedFeatures = []
        features.forEach((feature) => {
            if (feature.properties.attached_object_uuid && seenUuids.has(feature.properties.attached_object_uuid)) {
                return
            }
            seenUuids.add(feature.properties.attached_object_uuid)

            const referentialRoadGeometries = getReferentialRoadGeometries(referential, feature)

            const finalGeometry =
                feature.properties.attached_object_content_type === NETWORK_ATTACHMENT ? turf.center(turf.bboxPolygon(selectedNetworkBoundingBox)).geometry
                    : feature.geometry.type !== "Point"
                        ? findNearestPointOnRoad(feature, referentialRoadGeometries)
                        : feature.geometry

            sanitizedFeatures.push({
                id: feature.id,
                geometry: finalGeometry,
                properties: {
                    ...feature.properties,
                    geometry: finalGeometry,
                    icon: feature.properties.attached_object_content_type ?? feature.properties.linear_location_type.toLowerCase(),
                    linearLocation: feature.linear_location,
                    linear_location_type: feature.properties.linear_location_type?.toLowerCase(),
                    uuid: feature.id,
                },
            })
        })

        return turf.featureCollection(sanitizedFeatures) as GeoJSON.FeatureCollection<GeoJSON.Geometry>
    }, [data, referential, selectedNetworkBoundingBox])

    return <Source {...otherProps} type="geojson" data={sanitizedData} tolerance={.001} />
}

const getReferentialRoadGeometries = (referential, feature) => {
    return (
        referential.features
            .filter((road) => road.linear_location[0].road === feature?.linear_location?.[0]?.road)
            .map((road) => road.geometry) || []
    )
}
const findNearestPointOnRoad = (feature, roadGeometries) => {
    if (!roadGeometries.length) {
        return turf.centroid(feature).geometry
    }

    const centroid = turf.centroid(feature).geometry
    const lines = roadGeometries.map((g) => turf.lineString(g.coordinates))
    const combined = turf.combine(turf.featureCollection(lines))
    const multiLineFeature = combined.features[0]

    return turf.nearestPointOnLine(
        multiLineFeature as turf.Feature<turf.MultiLineString>,
        centroid
    ).geometry
}
